import { useContext, useEffect, useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'

import { AuthContext } from '../context/AuthContext'
import StudentSignUp from '../component/student-components/StudentSignUp'
import { useAfterSignInPath } from '../tool/returnPath'

// Standalone /signup page - the general "create an account" entry point, reusing the same
// shell/design as Login.jsx (same CSS classes from login.scss) and the same StudentSignUp
// form used when a specific /workspace/:id link requires an account (see Login.jsx). The
// previous version of this page asked for a College + CRN pairing that was never wired to
// anything (getAvailableCRNs returned a hardcoded placeholder list) and didn't visually
// match the sign-in screen - removed per explicit request rather than carried forward.
const SignUp = () => {
  const { currentUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark'
    const stored = window.localStorage.getItem('vzTheme') || 'dark'
    document.documentElement.setAttribute('data-vz-theme', stored)
    return stored
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-vz-theme', theme)
    window.localStorage.setItem('vzTheme', theme)
  }, [theme])

  // Same redirect-after-auth pattern as Login.jsx - lands back wherever the visitor was
  // trying to go if they arrived here via a redirect (e.g. a gated /workspace/:id link),
  // otherwise on the page the sign-in guard kept for this browser (tool/returnPath.js), if
  // any, and then falls back to /session.
  const afterSignInPath = useAfterSignInPath()
  useEffect(() => {
    if (!currentUser) return
    const from = afterSignInPath(location.state?.from?.pathname)
    navigate(from, { replace: true })
  }, [currentUser, location.state, navigate, afterSignInPath])

  return (
    <div className="login-page">
      <header className="login-nav">
        <Link to="/about" className="login-brand" aria-label="VizPI home">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <circle className="login-mark-pulse" cx="10" cy="4" r="2.4" />
            <circle cx="4" cy="15" r="2.4" opacity="0.55" />
            <circle cx="16" cy="15" r="2.4" opacity="0.55" />
          </svg>
          <span>VizPI</span>
        </Link>
        <button
          type="button"
          className="login-theme-toggle"
          onClick={() => setTheme(current => current === 'dark' ? 'light' : 'dark')}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          {theme === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <line x1="12" y1="2" x2="12" y2="4" />
              <line x1="12" y1="20" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
              <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="4" y2="12" />
              <line x1="20" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
              <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
      </header>

      <main className="login-shell">
        <section className="login-story" aria-labelledby="signup-story-title">
          <div className="login-eyebrow"><span /> Programming learning, made visible</div>
          <h1 id="signup-story-title">See the process.<br /><em>Support the learner.</em></h1>
          <p>VizPI connects coding activity, testing, and help-seeking so instructors can understand how students learn—not only what they submit.</p>
          <div className="login-signal-card" aria-hidden="true">
            <div className="login-signal-header"><span>LIVE LEARNING TRACE</span><span className="login-live-dot">Recording</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:08</span><span className="trace-kind is-code">CODE</span><span>Edited event handler</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:14</span><span className="trace-kind is-render">RENDER</span><span>Preview updated</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:21</span><span className="trace-kind is-help">HELP</span><span>Asked for a hint</span></div>
          </div>
        </section>

        <section className="login-card" aria-labelledby="signup-title">
          <div className="login-card-heading">
            <span className="login-kicker">Welcome</span>
            <h2 id="signup-title">Create your VizPI account</h2>
            <p>Set up a student account to track your progress across exercises.</p>
          </div>
          <StudentSignUp />
          <p className="login-signup">Already have an account? <Link to="/login" state={{ from: location.state?.from }}>Sign in</Link></p>
        </section>
      </main>
    </div>
  )
}

export default SignUp
